function normalizeChartPayload (data) {
  if (data && Array.isArray(data.datasets)) {
    return {
      chartData: data.datasets,
      labels: data.labels || null,
      metadata: data.metadata || null
    }
  }
  return {
    chartData: data,
    labels: null,
    metadata: null
  }
}

function normalizeTextualPayload (data) {
  if (data && data.metadata && (data.columns || data.rows)) {
    return {
      report: {
        columns: data.columns,
        rows: data.rows
      },
      metadata: data.metadata
    }
  }
  return {
    report: data,
    metadata: data && data.metadata ? data.metadata : null
  }
}

export default {
  textual (data, title) {
    const normalized = normalizeTextualPayload(data)
    return {
      report: normalized.report,
      metadata: normalized.metadata,
      title: title
    }
  },
  chart (data, title) {
    const normalized = normalizeChartPayload(data)
    const labels = (normalized.labels || createChartLabels(normalized.chartData)).map(String)
    const formatted = {
      report: {
        labels: labels,
        datasets: createChartDatasets(normalized.chartData, labels)
      },
      metadata: normalized.metadata,
      options: buildChartOptions(title, normalized.metadata)
    }
    return formatted
  },
  horizontalBarChart (data, title) {
    const chart = this.chart(data, title)

    chart.options.scales.yAxes = [{
      ticks: {
        autoSkip: false,
        callback: function (yLabel) {
          return truncate(yLabel, 40)
        }
      }
    }]

    chart.options.scales.xAxes = [{
      ticks: {
        callback: (value) => {
          const num = Number(value)
          if (!Number.isFinite(num)) {
            return ''
          }
          return num.toLocaleString('pt-BR')
        }
      }
    }]

    chart.options.tooltips.callbacks.label = (tooltipItems) => {
      const num = Number(tooltipItems.xLabel)
      if (!Number.isFinite(num)) {
        return 'Sem dado'
      }
      return num.toLocaleString('pt-BR')
    }

    return chart
  },
  percentageHorizontalBarChart (data, title) {
    const chart = this.horizontalBarChart(data, title)

    chart.options.scales.xAxes[0].ticks.max = 100
    chart.options.scales.xAxes[0].ticks.callback = (value) => {
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return ''
      }
      return num.toFixed(0) + '%'
    }

    chart.options.tooltips.callbacks.label = (tooltipItems) => {
      const num = Number(tooltipItems.xLabel)
      if (!Number.isFinite(num)) {
        return 'Sem dado'
      }
      return num.toFixed(1).replace('.', ',') + '%'
    }

    chart.options.legend = { display: false }

    return chart
  },
  doughnutChart (data, title) {
    const normalized = normalizeChartPayload(data)
    const labels = createChartLabels(normalized.chartData)
    const reportDatasets = createChartDatasets(normalized.chartData, labels)
    reportDatasets[0].borderColor = colors
    reportDatasets[0].backgroundColor = colors

    const chart = this.chart(data, title)
    chart.report.labels = labels
    chart.report.datasets = reportDatasets
    chart.options.scales = false
    chart.options.maintainAspectRatio = true
    chart.options.tooltips.callbacks.label = (tooltipItems, data) => {
      const idx = tooltipItems.index
      const value = data.datasets[0].data[idx]
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return 'Sem dado'
      }
      return num.toLocaleString('pt-BR')
    }

    return chart
  },
  percentageDoughnutChart (data, title) {
    const chart = this.doughnutChart(data, title)
    const categoryCount = createChartLabels(normalizeChartPayload(data).chartData).length

    chart.options.tooltips.callbacks.label = (tooltipItems, data) => {
      const idx = tooltipItems.index
      const value = data.datasets[0].data[idx]
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return 'Sem dado'
      }
      return data.labels[idx] + ': ' + num.toFixed(1).replace('.', ',') + '%'
    }

    chart.options.legend = {
      display: true,
      position: categoryCount > 5 ? 'right' : 'bottom',
      labels: {
        boxWidth: 14,
        fontSize: 11,
        padding: 10
      }
    }

    return chart
  },
  stackedBarChart (data, title) {
    const chart = this.chart(data, title)
    chart.options.scales.xAxes = [{
      stacked: true
    }]
    chart.options.scales.yAxes = [{
      stacked: true
    }]
    chart.options.tooltips.callbacks.title = (tooltipItems, data) => {
      const idx = tooltipItems[0].datasetIndex
      return data.datasets[idx].label + ' - ' + tooltipItems[0].label
    }
    return chart
  }
}

function buildChartOptions (title, metadata) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    title: {
      display: true,
      text: title
    },
    scales: {
      yAxes: [{
        ticks: {
          callback: (value, index, values) => {
            const num = Number(value)
            if (!Number.isFinite(num)) {
              return ''
            }
            return num.toLocaleString('pt-BR')
          }
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          maxRotation: 60,
          minRotation: 0,
          callback: function (xLabel) {
            return truncate(xLabel, 25)
          }
        }
      }]
    },
    tooltips: {
      enabled: true,
      callbacks: {
        title: (tooltipItems, data) => {
          const idx = tooltipItems[0].index
          return data.labels[idx]
        },
        label: (tooltipItems, data) => {
          const num = Number(tooltipItems.yLabel)
          if (!Number.isFinite(num)) {
            return 'Sem dado'
          }
          return num.toLocaleString('pt-BR')
        }
      }
    }
  }

  if (metadata && metadata.filterScopeLabel) {
    options.title.text = title + '\n' + metadata.filterScopeLabel
  }

  return options
}

const createChartLabels = function (chartData) {
  const nonEmptyDataset = chartData.find(dataset => Object.keys(dataset.result).length)
  if (!nonEmptyDataset) {
    return []
  }
  return Object.keys(nonEmptyDataset.result).map(stripTags)
}

const stripTags = function (str) {
  return String(str).replace(/<(?:.|\n)*?>/gm, '')
}

const truncate = function (str, maxLength = 30) {
  const value = String(str)
  if (value.length <= maxLength) {
    return value
  }
  return value.substr(0, maxLength).trim() + '...'
}

const colors = [
  '#3c8dbc', '#00c0ef', '#f56954',
  '#3c4dbc', '#3cbcab', '#bc3c4d',
  '#bd3ca1', '#f3d512', '#1f1f1f',
  '#214034', '#351224', '#b99f52',
  '#424240', '#66787b', '#643c24'
]

const normalizeChartValue = function (value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (typeof value === 'object' && value !== null && value.value !== undefined) {
    return normalizeChartValue(value.value)
  }

  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const createChartDatasets = function (chartData, labels) {
  let datasets = []
  chartData.forEach(function (item, index) {
    const result = item.result || {}
    const data = labels && labels.length
      ? labels.map(function (label) {
        if (result[label] !== undefined) {
          return normalizeChartValue(result[label])
        }
        const key = String(label)
        if (result[key] !== undefined) {
          return normalizeChartValue(result[key])
        }
        return null
      })
      : Object.values(result).map(normalizeChartValue)

    let dataset = {
      label: truncate(String(item.label), 45),
      borderColor: colors[index % colors.length],
      backgroundColor: colors[index % colors.length],
      fill: false,
      data: data,
      spanGaps: false
    }
    datasets.push(dataset)
  })

  return datasets
}



// WEBPACK FOOTER //
// ./src/services/report_formatter.js